import React, { useMemo, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import axios from "axios";
import debounce from "lodash/debounce";
import { getDecryptedCookie } from "../../utils";

const filter = createFilterOptions();

export default function FreeSoloCreateOption({
  options: initialStaticOptions = [],
  label,
  name,
  value,
  onBlur,
  error,
  helperText,
  required,
  setFieldValue,
  InputProps,
  disabled,
  onSubmit,
  ...props
}) {
  const [staticOptions, setStaticOptions] =
    React.useState(initialStaticOptions);
  const [dynamicOptions, setDynamicOptions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [selectedValue, setSelectedValue] = React.useState(""); // Track selected value
  const [language, setLanguage] = useState("Tamil"); // Initialize language with default 'Tamil'

  const apiTamil =
    "https://inputtools.google.com/request?text&itc=ta-t-i0-und&num=100&cp=0&cs=0&ie=utf-8&oe=utf-8&app=demopage";
  const apiEnglish = null; // Replace with your English API endpoint

  // Set language based on the cookie value
  useEffect(() => {
    const cookieLanguage = getDecryptedCookie("lll");
    setLanguage(
      cookieLanguage === "en" || cookieLanguage === "enta" ? "English" : "Tamil"
    );
  }, []);

  const fetchSuggestions = async (input) => {
    if (input && language === "Tamil") {
      // Only fetch suggestions if language is Tamil
      const inputParts = input.split(/\s+/); // Split by spaces

      // Identify parts that are alphabetic (eligible for translation) vs numbers
      const partsToTranslate = inputParts
        .map((part) => (/^[a-zA-Z]+$/.test(part) ? part : null)) // Translate only alphabetic parts
        .filter(Boolean)
        .join(" "); // Join the alphabetic parts into a string for translation

      try {
        const api = language === "Tamil" ? apiTamil : apiEnglish;
        let translatedText = partsToTranslate;

        if (partsToTranslate.trim()) {
          // Call API only if there is something to translate
          const response = await axios.get(api, {
            params: { text: partsToTranslate },
          });
          const suggestions = response.data[1][0][1];
          translatedText = suggestions[0] || partsToTranslate; // Fallback if no translation is available
        }

        // Split translated text into words for recombining
        const translatedParts = translatedText.split(/\s+/);

        // Rebuild the input by combining the original parts with translated alphabetic words
        const rebuiltInput = inputParts
          .map((part) => {
            if (/^[a-zA-Z]+$/.test(part)) {
              return translatedParts.shift(); // Replace alphabetic words with their translated version
            }
            return part; // Keep numbers and alphanumeric parts as-is
          })
          .join(" ");

        // Set dynamic options for Autocomplete
        const newOptions = [
          {
            title: rebuiltInput,
            value: rebuiltInput,
          },
        ];

        setDynamicOptions(newOptions);
        return newOptions;
      } catch (error) {
        console.error("Error fetching data:", error);
        setDynamicOptions([]);
        return [];
      }
    } else {
      // If language is not Tamil (e.g., English), clear dynamic options
      setDynamicOptions([]);
      return [];
    }
  };

  const debouncedFetchSuggestions = useMemo(
    () =>
      debounce(async (input) => {
        await fetchSuggestions(input);
      }, 500),
    [language] // Ensure debounce resets when language changes
  );

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    debouncedFetchSuggestions(newInputValue);
  };

  const handleChange = async (event, newValue) => {
    let newValueStr;
    if (typeof newValue === "string") {
      newValueStr = newValue;
    } else if (newValue) {
      if (newValue.inputValue) {
        newValueStr = newValue.inputValue;
        const updatedStaticOptions = [
          ...staticOptions,
          { title: newValueStr, value: newValueStr },
        ];
        setStaticOptions(updatedStaticOptions);
        setFieldValue(name, newValueStr);
        setSelectedValue(newValueStr); // Update selected value
        return;
      }
      newValueStr = newValue.title;
    } else {
      newValueStr = "";
    }

    if (setFieldValue) {
      setFieldValue(name, newValueStr);
    }
    setSelectedValue(newValueStr); // Update selected value
  };

  const allOptions = [...staticOptions, ...dynamicOptions];

  const handleSubmit = () => {
    let finalValue = inputValue; // Default to input value
    if (language === "Tamil") {
      finalValue = selectedValue
        ? `${selectedValue} ${inputValue}`
        : inputValue;
    }
    if (onSubmit) {
      onSubmit(finalValue); // Pass the formatted value on submit
    }
  };

  return (
    <div>
      {language === "Tamil" ? (
        // Only render Autocomplete when language is Tamil
        <Autocomplete
          {...props}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;

            if (inputValue) {
              return options;
            }

            const isExisting = options.some(
              (option) => inputValue === option.title
            );
            if (inputValue !== "" && !isExisting) {
              filtered.push({
                inputValue,
                title: `Add "${inputValue}"`,
              });
            }
            return filtered;
          }}
          onInputChange={handleInputChange}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          options={allOptions}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            return option.title;
          }}
          freeSolo
          renderInput={(params) => (
            <TextField
              {...params}
              id={name}
              name={name}
              label={label}
              onBlur={onBlur}
              error={error}
              helperText={helperText}
              required={required}
              disabled={disabled}
              InputProps={{ ...params.InputProps, ...InputProps }}
              {...props}
              fullWidth
            />
          )}
        />
      ) : (
        // Render TextField when language is not Tamil (e.g., English or enta)
        <TextField
          {...props}
          id={name}
          name={name}
          label={label}
          onBlur={onBlur}
          value={value}
          error={error}
          helperText={helperText}
          required={required}
          disabled={disabled}
          InputProps={InputProps}
          fullWidth
        />
      )}
    </div>
  );
}
